import * as React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Box,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
  Switch,
  Fab,
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp, Check } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import OrderDetail from 'types/models/OrderDetail';
import { getLuxuryBoxMovements, payLuxuryBoxMovements } from 'services/venuesServices';
import AppContext from 'contexts/AppContext';
import EditVenueContext from 'contexts/EditVenueContext';
import { parseDateTime } from 'utils/datetime';
import { formatMoneyAuto } from 'utils/formatMoney';
import BooleanIcon from 'components/common/BooleanIcon';
import ConfirmDialog from 'components/ConfirmDialog';
import LuxuryBoxMovement from 'types/models/LuxuryBoxMovement';

export default (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const { selectedLuxuryBox } = React.useContext(EditVenueContext);
  const [openCollapseDetail, setOpenCollapseDetail] = React.useState<string>('');
  const [movements, setMovements] = React.useState<LuxuryBoxMovement[]>([]);
  const [checkedMovements, setCheckedMovements] = React.useState<Record<string, number>>({});
  const [checkAll, setCheckAll] = React.useState<boolean>(false);
  const listContainerRef = React.useRef<HTMLDivElement | null>(null);
  const numberOfMovementsChecked = Object.keys(checkedMovements).length;
  const totalAmountToPayByCheckMovements = Object.values(checkedMovements).reduce((a, b) => a + b, 0);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const currentLocalPage = React.useRef<number>(1);

  function debounce<Func extends (...args: any[]) => void>(func: Func, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<Func>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  const loadLuxuryBoxMovements = React.useCallback(
    async (luxuryBoxMovementId: string, page?: number) => {
      const paginationData = await getLuxuryBoxMovements(luxuryBoxMovementId, page);
      if (paginationData && paginationData.data.length > 0) {
        if (paginationData.currentPage === 1) {
          setMovements(paginationData.data);
        } else {
          currentLocalPage.current += 1;
          setMovements([...movements, ...paginationData.data]);
        }
      }
    },
    [currentPageListNumber],
  );

  const onScroll = () => {
    const divElement = listContainerRef.current;
    if (divElement) {
      //const { scrollTop, scrollHeight, clientHeight } = divElement;
      const isNearBottom = divElement.getBoundingClientRect().bottom <= window.innerHeight;
      if (isNearBottom && currentPageListNumber) {
        setCurrentPageListNumber(currentLocalPage.current + 1);
      }
    }
  };

  const handleCollapse = (id: string) => {
    if (openCollapseDetail === id) {
      setOpenCollapseDetail('');
    } else {
      setOpenCollapseDetail(id);
    }
  };

  const handleSelectAll = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!movements.length) return;
    setCheckAll(checked);

    if (checked) {
      const reduceAllMovementsToObject: Record<string, number> = movements.reduce((acc, value) => {
        acc[`${value.id}`] = typeof value.order.total === 'string' ? parseFloat(value.order.total) : value.order.total;
        return acc;
      }, {} as Record<string, number>);
      setCheckedMovements(reduceAllMovementsToObject);
    } else {
      setCheckedMovements({});
    }
  };

  const onCheckedMovement = (id: string, orderTotal: number) => {
    return () => {
      if (typeof checkedMovements[`${id}`] === 'number') {
        const movementsCopy = Object.assign({}, checkedMovements);
        delete movementsCopy[`${id}`];
        setCheckedMovements(movementsCopy);
      } else {
        setCheckedMovements({
          ...checkedMovements,
          [`${id}`]: orderTotal,
        });
      }
    };
  };

  const onConfirm = async (): Promise<void> => {
    if (!selectedLuxuryBox) return;
    if (!numberOfMovementsChecked) return;
    try {
      const movementIds = Object.keys(checkedMovements);
      const result = await payLuxuryBoxMovements(selectedLuxuryBox.id, movementIds);
      if (result) {
        history.go(-1);
        if (currentPageListNumber !== 1) setCurrentPageListNumber(1);
      } else {
        throw new Error('Error paying luxury box movements');
      }
    } catch (error: any) {
      alert(`ErrorOnPayLuxuryBoxMovements: ${error.message}`);
    }
  };

  React.useEffect(() => {
    if (selectedLuxuryBox && currentPageListNumber) {
      loadLuxuryBoxMovements(selectedLuxuryBox.id, currentPageListNumber);
    }
  }, [currentPageListNumber]);

  React.useEffect(() => {
    if (!selectedLuxuryBox) {
      history.push('/private/venues');
    }
  }, [selectedLuxuryBox]);

  React.useEffect(() => {
    document.addEventListener('scroll', debounce(onScroll, 100));
    // Clean-up
    return () => {
      setCurrentPageListNumber(1);
      if (submitting) setSubmitting(false);
      document.removeEventListener('scroll', onScroll);
    };
  }, []);

  const renderContent = (movement: LuxuryBoxMovement, index: string): JSX.Element => {
    return (
      <React.Fragment key={index}>
        <TableRow className={'content-table'}>
          <TableCell>{t('luxuryBoxesMovements-page-not-covered')}</TableCell>
          <TableCell align="left">{movement.order.id}</TableCell>
          <TableCell>{formatMoneyAuto(movement.amount)}</TableCell>
          <TableCell>
            <IconButton aria-label="expand row" size="small" onClick={() => handleCollapse(movement.id)}>
              {movement.id === openCollapseDetail ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
          <TableCell align="left">
            <Checkbox
              checked={typeof checkedMovements[`${movement.id}`] === 'number'}
              onChange={onCheckedMovement(
                movement.id,
                typeof movement.order.total === 'string' ? parseFloat(movement.order.total) : movement.order.total,
              )}
            />
          </TableCell>
          <TableCell>{parseDateTime(movement.updatedAt)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
            <Collapse in={movement.id === openCollapseDetail} timeout="auto" unmountOnExit={true}>
              <Box margin={1}>
                <Typography variant="h6" gutterBottom={true} component="div">
                  {t('consumptionsReport-page-detail')}
                </Typography>
                <Table size="small" aria-label="details">
                  <TableHead className="table-header">
                    <TableRow>
                      <TableCell>{t('consumptionsReport-page-product')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-amount')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-clothingSizeType')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-unitPrice')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-totalProduct')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-isBenefitProduct')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {movement.order.details.map((detail: OrderDetail) => (
                      <TableRow key={detail.id}>
                        <TableCell component="th" scope="row">
                          {typeof detail.product === 'string' ? detail.product : (detail.product as any)?.name}
                        </TableCell>
                        <TableCell>{detail.quantity}</TableCell>
                        <TableCell>{detail.clothinSizeType?.description ?? '-'}</TableCell>
                        <TableCell>{formatMoneyAuto(detail.price)}</TableCell>
                        <TableCell>{formatMoneyAuto(detail.totalAmount)}</TableCell>
                        <TableCell>
                          <BooleanIcon value={detail.fullProduct?.isUsedForTomorrowBenefit} showText />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <div>
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={onConfirm}
        title={t('luxuryBoxesMovements-page-confirm-modal-title', {
          numberOfMovements: numberOfMovementsChecked,
          amount: totalAmountToPayByCheckMovements,
        })}
      />
      <div className="title-container">
        <Typography variant="h6" component="div">
          {`${t('luxuryBoxesMovements-page-title', {
            floor: selectedLuxuryBox?.floor,
            identifierOnFloor: selectedLuxuryBox?.identifierOnFloor,
          })}`}
        </Typography>
        <FormControlLabel
          id="checkAll"
          name="checkAll"
          control={<Switch checked={checkAll} onChange={handleSelectAll} />}
          label={t('luxuryBoxesMovements-page-check-all')}
          disabled={submitting}
        />
      </div>
      <div ref={listContainerRef}>
        <TableContainer className="table-container" component={Paper}>
          <Table>
            <TableHead className="table-header">
              <TableRow>
                <TableCell>{t('luxuryBoxesMovements-page-type')}</TableCell>
                <TableCell>{t('luxuryBoxesMovements-page-order')}</TableCell>
                <TableCell>{t('luxuryBoxesMovements-page-amount')}</TableCell>
                <TableCell>{t('luxuryBoxesMovements-page-detail')}</TableCell>
                <TableCell>{t('luxuryBoxesMovements-page-select')}</TableCell>
                <TableCell>{t('luxuryBoxesMovements-page-date')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {movements.length > 0 ? (
                movements.map((movement: LuxuryBoxMovement) => renderContent(movement, movement.id))
              ) : (
                <TableRow>
                  <TableCell>{t('consumptionsReport-page-noOperations')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {numberOfMovementsChecked > 0 && (
          <Fab
            variant="extended"
            color="primary"
            style={{
              position: 'fixed',
              bottom: 16, // Adjust bottom margin as needed
              right: 16, // Adjust right margin as needed
            }}
            disabled={openDialog || submitting}
            onClick={() => setOpenDialog(true)}
          >
            <Check />
            {t('luxuryBoxesMovements-page-pay-button-title', {
              numberOfMovements: numberOfMovementsChecked,
              amount: totalAmountToPayByCheckMovements,
            })}
          </Fab>
        )}
      </div>
    </div>
  );
};
